<template>
  <div class="p2p-page">
    <div class="p2p-stack" style="gap:6px">
      <h1 class="p2p-h1">Support</h1>
      <p class="p2p-lead">Find a member's work, see what happened and fix it. Every action needs a reason and is recorded.</p>
    </div>
    <div class="p2p-tabs" role="tablist">
      <button type="button" role="tab" :aria-selected="tab === 'members' ? 'true' : 'false'" :class="{ on: tab === 'members' }" @click="tab = 'members'">Members</button>
      <button type="button" role="tab" :aria-selected="tab === 'events' ? 'true' : 'false'" :class="{ on: tab === 'events' }" @click="openEvents">Webhook events</button>
    </div>
    <div v-if="error" class="p2p-alert bad">{{ error }}</div>
    <div v-if="notice" class="p2p-alert good">{{ notice }}</div>

    <template v-if="tab === 'members'">
      <form class="p2p-row" style="align-items:flex-end" @submit.prevent="runSearch">
        <label class="p2p-label" style="flex:1 1 320px">Email, member id, project, post or job id, provider post id or publish group
          <input v-model="query" class="p2p-input" placeholder="name@example.com or 496">
        </label>
        <button type="submit" class="p2p-btn" :disabled="!query.trim() || busy">Search</button>
      </form>

      <section v-if="results" class="p2p-stack" style="gap:8px">
        <div v-if="!resultCount" class="p2p-muted">Nothing found.</div>
        <button v-for="item in results.members" :key="'m' + item.id" type="button" class="p2p-tile p2p-result" @click="openMember(item.id)">
          <strong>{{ item.email }}</strong><span class="p2p-faint">Member {{ item.id }} · {{ item.plan }}{{ item.guest ? ' · guest' : '' }}</span>
        </button>
        <button v-for="item in results.projects" :key="'p' + item.id" type="button" class="p2p-tile p2p-result" @click="openMember(item.user_id)">
          <strong>Project {{ item.id }} · {{ item.name }}</strong><span class="p2p-faint">Member {{ item.user_id }}{{ item.deleted ? ' · deleted' : '' }}</span>
        </button>
        <button v-for="item in results.deliveries" :key="'d' + item.id" type="button" class="p2p-tile p2p-result" @click="openMember(item.user_id)">
          <strong>Post delivery {{ item.id }} · {{ item.platform }} · {{ item.state }}</strong><span class="p2p-faint">Member {{ item.user_id }} · {{ item.publish_group }}</span>
        </button>
        <button v-for="item in results.operations" :key="'o' + item.id" type="button" class="p2p-tile p2p-result" @click="openMember(item.user_id)">
          <strong>Job record {{ item.id }} · {{ item.kind }} · {{ item.status }}</strong><span class="p2p-faint">Member {{ item.user_id }} · project {{ item.project_id }}</span>
        </button>
        <button v-for="item in results.accounts" :key="'a' + item.id" type="button" class="p2p-tile p2p-result" @click="openMember(item.user_id)">
          <strong>{{ item.platform }} account {{ item.username }}</strong><span class="p2p-faint">Member {{ item.user_id }} · {{ item.status }}</span>
        </button>
      </section>

      <div v-if="loadingMember" class="p2p-skeleton" style="height:240px"></div>
      <template v-else-if="detail">
        <section class="p2p-card">
          <div class="p2p-between">
            <div class="p2p-stack" style="gap:4px">
              <h2 class="p2p-h2">{{ detail.member.email }}</h2>
              <span class="p2p-faint">Member {{ detail.member.id }} · {{ detail.member.plan }} · {{ detail.member.available_credits }} credits available
                <span v-if="Number(detail.member.reserved_credits)">· {{ detail.member.reserved_credits }} reserved</span>
                · {{ detail.member.verified ? 'verified' : 'not verified' }}{{ detail.member.guest ? ' · guest' : '' }}</span>
            </div>
            <div class="p2p-row">
              <button type="button" class="p2p-btn sm soft" :disabled="busy" @click="ask('Pause every autopilot for this member', { action: 'automations', status: 'paused' })">Pause autopilots</button>
              <button type="button" class="p2p-btn sm ghost" :disabled="busy" @click="ask('Resume the autopilots support paused', { action: 'automations', status: 'active' })">Resume</button>
            </div>
          </div>
        </section>

        <section class="p2p-card">
          <h3 class="p2p-h3">Projects</h3>
          <table class="p2p-table">
            <thead><tr><th>Id</th><th>Name</th><th>Autopilot</th><th>Created</th></tr></thead>
            <tbody><tr v-for="item in detail.projects" :key="item.id"><td class="p2p-mono">{{ item.id }}</td>
              <td>{{ item.name }}<span v-if="item.deleted" class="p2p-faint"> (deleted)</span></td>
              <td>{{ item.automation ? item.automation.status + (item.automation.pause_reason ? ' · ' + item.automation.pause_reason : '') : '—' }}</td>
              <td class="p2p-faint">{{ when(item.created_at) }}</td></tr></tbody>
          </table>
        </section>

        <section class="p2p-card">
          <h3 class="p2p-h3">Post deliveries</h3>
          <div class="p2p-table-scroll">
            <table class="p2p-table">
              <thead><tr><th>Id</th><th>Network</th><th>State</th><th>Provider</th><th>Attempts</th><th>Credits</th><th>Time</th><th></th></tr></thead>
              <tbody><tr v-for="item in detail.deliveries" :key="item.id">
                <td class="p2p-mono">{{ item.id }}</td><td>{{ item.platform }}<span class="p2p-faint"> {{ item.account }}</span></td>
                <td><span :class="['p2p-chip', tone(item.state)]">{{ item.state }}</span><div v-if="item.error" class="p2p-faint">{{ item.error }}</div></td>
                <td class="p2p-mono p2p-faint">{{ item.provider_post_id || '—' }}<div v-if="item.public_url"><a :href="item.public_url" target="_blank" rel="noopener">link</a></div></td>
                <td class="p2p-mono">{{ item.attempts || 0 }}</td>
                <td class="p2p-mono">{{ item.credits_charged || '0' }}</td>
                <td class="p2p-faint">{{ when(item.published_at || item.scheduled_at) }}</td>
                <td><div class="p2p-row" style="gap:4px;flex-wrap:nowrap">
                  <button v-if="item.state === 'failed' || item.state === 'retried'" type="button" class="p2p-btn sm ghost" :disabled="busy"
                          @click="ask('Ask the provider what happened to delivery ' + item.id, { action: 'reconcile', id: item.id })">Reconcile</button>
                  <button v-if="Number(item.credits_charged) > 0" type="button" class="p2p-btn sm ghost" :disabled="busy"
                          @click="ask('Refund ' + creditWords(item.credits_charged) + ' for delivery ' + item.id, { action: 'refund', kind: 'delivery', id: item.id })">Refund</button>
                  <button v-if="item.result" type="button" class="p2p-btn sm ghost" @click="inspect = item.result">Response</button>
                </div></td>
              </tr></tbody>
            </table>
          </div>
        </section>

        <section class="p2p-card">
          <h3 class="p2p-h3">Work</h3>
          <div class="p2p-table-scroll">
            <table class="p2p-table">
              <thead><tr><th>Id</th><th>Kind</th><th>Status</th><th>Job</th><th>Credits</th><th>Created</th><th></th></tr></thead>
              <tbody><tr v-for="item in detail.operations" :key="item.id">
                <td class="p2p-mono">{{ item.id }}</td><td>{{ item.kind }}<span class="p2p-faint"> · project {{ item.project_id }}</span></td>
                <td><span :class="['p2p-chip', tone(item.status)]">{{ item.status }}</span><div v-if="item.error" class="p2p-faint">{{ item.error }}</div></td>
                <td class="p2p-mono p2p-faint">{{ item.job_id || '—' }}</td><td class="p2p-mono">{{ item.credits_charged || '0' }}</td>
                <td class="p2p-faint">{{ when(item.created_at) }}</td>
                <td><div class="p2p-row" style="gap:4px;flex-wrap:nowrap">
                  <button type="button" class="p2p-btn sm ghost" :disabled="busy" @click="openJob(item.id)">Inspect</button>
                  <button v-if="Number(item.credits_charged) > 0" type="button" class="p2p-btn sm ghost" :disabled="busy"
                          @click="ask('Refund ' + creditWords(item.credits_charged) + ' for ' + item.kind + ' ' + item.id, { action: 'refund', kind: 'operation', id: item.id })">Refund</button>
                </div></td>
              </tr></tbody>
            </table>
          </div>
        </section>

        <section class="p2p-card">
          <h3 class="p2p-h3">Connected accounts</h3>
          <div v-if="!detail.accounts.length" class="p2p-muted">None.</div>
          <div v-for="item in detail.accounts" :key="item.id" class="p2p-row" style="gap:8px">
            <strong>{{ item.label }}</strong><span class="p2p-faint">{{ item.username }} · {{ item.status }}<span v-if="item.status_detail"> · {{ item.status_detail }}</span></span>
            <span class="p2p-mono p2p-faint">{{ item.provider_account_id }}</span>
          </div>
        </section>

        <section class="p2p-card">
          <h3 class="p2p-h3">Credit ledger</h3>
          <div v-if="!detail.ledger.length" class="p2p-muted">No credit movements yet.</div>
          <div v-else class="p2p-table-scroll">
            <table class="p2p-table">
              <thead><tr><th>Amount</th><th>Type</th><th>What</th><th>Job</th><th>When</th></tr></thead>
              <tbody><tr v-for="item in detail.ledger" :key="item.id">
                <td class="p2p-mono" :class="{ 'p2p-bad': Number(item.amount) < 0 }">{{ Number(item.amount) > 0 ? '+' : '' }}{{ item.amount }}</td>
                <td>{{ item.type }}</td><td class="p2p-faint">{{ item.description }}</td>
                <td class="p2p-mono p2p-faint">{{ item.job_id || '—' }}</td><td class="p2p-faint">{{ when(item.created_at) }}</td>
              </tr></tbody>
            </table>
          </div>
        </section>

        <section class="p2p-card">
          <h3 class="p2p-h3">Audit trail</h3>
          <ol class="p2p-activity">
            <li v-for="item in detail.audit" :key="item.id">
              <span class="p2p-activity-dot" aria-hidden="true"></span>
              <div class="p2p-stack" style="gap:2px;min-width:0">
                <span class="p2p-text">{{ item.type.replace(/_/g, ' ') }}<span v-if="item.project_id" class="p2p-faint"> · project {{ item.project_id }}</span></span>
                <span class="p2p-faint p2p-mono" style="overflow-wrap:anywhere">{{ when(item.created_at) }} · {{ summary(item.details) }}</span>
              </div>
            </li>
          </ol>
        </section>
      </template>
    </template>

    <template v-else>
      <div class="p2p-row">
        <select v-model="eventStatus" class="p2p-select" style="width:auto" aria-label="Status" @change="loadEvents">
          <option value="all">All events</option><option value="received">Waiting</option><option value="processed">Processed</option><option value="failed">Failed</option>
        </select>
      </div>
      <div v-if="!events.length" class="p2p-empty"><strong>No webhook events yet</strong><span>Post for Me account and post updates appear here.</span></div>
      <div v-else class="p2p-table-scroll">
        <table class="p2p-table">
          <thead><tr><th>Id</th><th>Type</th><th>Status</th><th>Received</th><th>Data</th><th></th></tr></thead>
          <tbody><tr v-for="item in events" :key="item.id">
            <td class="p2p-mono">{{ item.id }}</td><td>{{ item.type }}</td><td><span :class="['p2p-chip', tone(item.status)]">{{ item.status }}</span></td>
            <td class="p2p-faint">{{ when(item.received_at) }}</td>
            <td class="p2p-mono p2p-faint" style="max-width:340px;overflow-wrap:anywhere">{{ JSON.stringify((item.payload || {}).data || {}) }}</td>
            <td><button type="button" class="p2p-btn sm ghost" :disabled="busy" @click="ask('Process webhook event ' + item.id + ' again', { action: 'replay', id: item.id })">Replay</button></td>
          </tr></tbody>
        </table>
      </div>
    </template>

    <v-dialog v-model="confirm.open" max-width="520">
      <div class="p2p-card raised">
        <h2 class="p2p-h2">{{ confirm.title }}</h2>
        <label class="p2p-label">Reason (kept in the audit trail)
          <textarea v-model="confirm.reason" class="p2p-textarea" rows="3" maxlength="240" placeholder="For example: member reported the post is live on X"></textarea>
        </label>
        <div class="p2p-row">
          <button type="button" class="p2p-btn" :disabled="confirm.reason.trim().length < 5 || busy" @click="perform">Confirm</button>
          <button type="button" class="p2p-btn ghost" @click="confirm.open = false">Cancel</button>
        </div>
      </div>
    </v-dialog>
    <v-dialog :value="!!job" max-width="720" @input="job = null">
      <div v-if="job" class="p2p-card raised p2p-job">
        <div class="p2p-between">
          <h2 class="p2p-h2">{{ job.kind }} {{ job.id }}</h2>
          <span :class="['p2p-chip', tone(job.status)]">{{ job.status }}</span>
        </div>
        <div class="p2p-faint">Project {{ job.project_id }}<span v-if="job.post_id"> · post {{ job.post_id }}</span><span v-if="job.run_id"> · autopilot run {{ job.run_id }}</span> · {{ creditWords(job.credits_charged || '0') }} charged</div>
        <div v-if="job.error" class="p2p-alert bad">{{ job.error }}</div>
        <h3 class="p2p-kicker">Timeline</h3>
        <ol class="p2p-activity">
          <li v-for="(item, i) in job.timeline" :key="i"><span class="p2p-activity-dot" aria-hidden="true"></span>
            <div class="p2p-stack" style="gap:2px"><span class="p2p-text">{{ item.what }}</span><span class="p2p-faint p2p-mono">{{ when(item.at) }}</span></div></li>
        </ol>
        <h3 class="p2p-kicker">Asked for</h3>
        <dl class="p2p-jobfacts"><template v-for="(value, key) in job.asked"><dt :key="'k' + key">{{ words(key) }}</dt><dd :key="'v' + key" class="p2p-mono">{{ show(value) }}</dd></template></dl>
        <h3 class="p2p-kicker">Made</h3>
        <dl class="p2p-jobfacts"><template v-for="(value, key) in job.made"><dt :key="'k' + key">{{ words(key) }}</dt><dd :key="'v' + key" class="p2p-mono">{{ show(value) }}</dd></template></dl>
        <template v-if="job.billing.length">
          <h3 class="p2p-kicker">Billing</h3>
          <div v-for="(item, i) in job.billing" :key="'b' + i" class="p2p-faint p2p-mono">{{ item.policy }} · {{ item.mode }} · quote {{ item.quote }} · {{ item.state }}<span v-if="item.outcome"> · {{ item.outcome }}</span><span v-if="Number(item.refunded)"> · refunded {{ item.refunded }}</span></div>
        </template>
        <template v-if="job.provider_calls.length">
          <h3 class="p2p-kicker">Provider calls</h3>
          <div v-for="(item, i) in job.provider_calls" :key="'c' + i" class="p2p-faint p2p-mono">{{ item.provider }} · {{ item.model }} · {{ item.step }} · {{ item.state }} · ${{ item.actual_usd || item.estimate_usd }}<span v-if="item.latency_ms"> · {{ item.latency_ms }} ms</span></div>
        </template>
        <button type="button" class="p2p-btn ghost" style="align-self:flex-start" @click="job = null">Close</button>
      </div>
    </v-dialog>
    <v-dialog :value="!!inspect" max-width="640" @input="inspect = null">
      <div class="p2p-card raised">
        <h2 class="p2p-h2">Stored provider response</h2>
        <pre class="p2p-mono p2p-faint" style="white-space:pre-wrap;overflow-wrap:anywhere;margin:0">{{ JSON.stringify(inspect, null, 2) }}</pre>
        <button type="button" class="p2p-btn ghost" style="align-self:flex-start" @click="inspect = null">Close</button>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import { get, post, message } from '../api';
import { localDate } from '../poll';

function requestKey() {
  const bytes = new Uint8Array(12);
  window.crypto.getRandomValues(bytes);
  return Array.prototype.map.call(bytes, b => b.toString(16).padStart(2, '0')).join('');
}

export default {
  name: 'AdminSupport',
  data: () => ({ tab: 'members', query: '', results: null, detail: null, memberId: null, loadingMember: false, busy: false,
    error: '', notice: '', events: [], eventStatus: 'all', inspect: null, job: null,
    confirm: { open: false, title: '', reason: '', action: null, key: '' } }),
  computed: {
    resultCount() { const r = this.results; return r ? r.members.length + r.projects.length + r.deliveries.length + r.operations.length + r.accounts.length : 0; }
  },
  mounted() {
    if (this.$route.query.member) this.openMember(Number(this.$route.query.member));
  },
  methods: {
    when(value) { return value ? localDate(value) : '—'; },
    creditWords(value) { return value + ' credit' + (Number(value) === 1 ? '' : 's'); },
    words(key) { return key.replace(/_/g, ' '); },
    show(value) { return value !== null && typeof value === 'object' ? JSON.stringify(value) : String(value); },
    async openJob(id) {
      this.error = '';
      try { this.job = await get('/admin/support/operations/' + id); } catch (error) { this.error = message(error); }
    },
    tone(state) { return { published: 'good', complete: 'good', processed: 'good', failed: 'bad', needs_credits: 'warn', received: 'warn', cancelled: '' }[state] || ''; },
    summary(details) {
      return Object.keys(details || {}).filter(key => key !== 'marker').map(key => key + ': ' + (typeof details[key] === 'object' ? JSON.stringify(details[key]) : details[key])).join(' · ');
    },
    async runSearch() {
      this.busy = true;
      this.error = '';
      try { this.results = await get('/admin/support/search', { q: this.query.trim() }); } catch (error) { this.error = message(error); } finally { this.busy = false; }
    },
    async openMember(id) {
      this.memberId = id;
      this.loadingMember = true;
      this.error = '';
      try {
        this.detail = await get('/admin/support/members/' + id);
        this.$router.replace({ query: { member: String(id) } }).catch(() => {});
      } catch (error) {
        this.error = message(error);
      } finally {
        this.loadingMember = false;
      }
    },
    async openEvents() { this.tab = 'events'; await this.loadEvents(); },
    async loadEvents() {
      try { this.events = (await get('/admin/support/provider-events', { status: this.eventStatus })).events; } catch (error) { this.error = message(error); }
    },
    ask(title, action) { this.confirm = { open: true, title, reason: '', action, key: requestKey() }; },
    async perform() {
      const { action, reason, key } = this.confirm;
      this.busy = true;
      this.error = '';
      this.notice = '';
      try {
        if (action.action === 'automations') {
          const result = await post('/admin/support/members/' + this.memberId + '/automations', { status: action.status, reason });
          this.notice = result.changed + ' autopilot' + (result.changed === 1 ? '' : 's') + (action.status === 'paused' ? ' paused.' : ' resumed.');
        } else if (action.action === 'reconcile') {
          const result = await post('/admin/support/deliveries/' + action.id + '/reconcile', { reason });
          this.notice = { published: 'The provider confirms it was published. Recorded free of charge.', not_found: 'The provider has no record of this post.',
            not_published: 'The provider has the post, but it was not published to this account.' }[result.outcome];
        } else if (action.action === 'refund') {
          const result = await post('/admin/support/refund', { kind: action.kind, target_id: action.id, reason, key });
          this.notice = result.refunded + (Number(result.refunded) === 1 ? ' credit' : ' credits') + ' returned to the member.';
        } else if (action.action === 'replay') {
          await post('/admin/support/provider-events/' + action.id + '/replay', { reason });
          this.notice = 'The event will be processed again within a minute.';
          await this.loadEvents();
        }
        this.confirm.open = false;
        if (this.memberId && action.action !== 'replay') await this.openMember(this.memberId);
      } catch (error) {
        this.error = message(error);
        this.confirm.open = false;
      } finally {
        this.busy = false;
      }
    }
  }
};
</script>

<style>
.p2p-result{flex-direction:column;align-items:flex-start!important;gap:2px;text-align:left;min-height:0!important;padding:10px 14px}
.p2p-table-scroll{overflow-x:auto}
.p2p-job{gap:12px;max-height:85vh;overflow-y:auto}
.p2p-jobfacts{display:grid;grid-template-columns:minmax(120px,auto) 1fr;gap:4px 14px;margin:0;font-size:13px}
.p2p-jobfacts dt{color:var(--p2p-muted)}
.p2p-jobfacts dd{margin:0;overflow-wrap:anywhere}
</style>
